import { ReactComponent as Load } from "assets/icons/hard-drive-upload.svg";
import { ReactComponent as Edit } from "assets/icons/pencil.svg";
import { ReactComponent as Delete } from "assets/icons/trash.svg";
import {
    DEFAULT_PAGE_SIZE_OPTION,
    handlePageClick,
    handlePageSizeChange,
} from "helpers/PaginationHelper";
import {
    BoolArrayFilter,
    Button,
    ConfirmationModal,
    CustomTable,
    NotificationPriority,
    NotificationPriorityTranslation,
    NotificationSeverity,
    NotificationSeverityTranslation,
    OptionType,
    Pagination,
    TableColumn,
    TableType,
} from "modules/common";
import { useDeleteNotificationTemplate, useGetNotificationTemplates } from "modules/communication";
import { CreateUpdateNotificationTemplateModal } from "modules/communication/components/CreateUpdateNotificationTemplateModal";
import { NotificationTemplatesFilters } from "modules/communication/components/NotificationTemplatesFilters";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Row } from "react-table";
import { AppRoutes } from "routing";
import { NotificationTemplateView } from "services/communication";
import { RootState } from "store";

interface Props {
    searchFilter: string;
    setSearchFilter: React.Dispatch<React.SetStateAction<string>>;
}

export const NotificationTemplates = ({ searchFilter, setSearchFilter }: Props) => {
    const history = useHistory();
    const profile = useSelector((state: RootState) => state.signIn.user);

    // filters
    const [authorFilter, setAuthorFilter] = useState<string[]>([]);
    const [severityFilter, setSeverityFilter] = useState<NotificationSeverity[]>([]);
    const [priorityFilter, setPriorityFilter] = useState<NotificationPriority[]>([]);
    const [professionalFieldFilter, setProfessionalFieldFilter] = useState<string[]>([]);
    const [isSharedFilter, setIsSharedFilter] = useState<BoolArrayFilter[]>([]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchFilter]);

    const [pageSize, setPageSize] = useState<OptionType>(DEFAULT_PAGE_SIZE_OPTION);
    const [currentPage, setCurrentPage] = useState(1);

    // modal state
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
    const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
    const [selectedNotificationTemplate, setSelectedNotificationTemplate] =
        useState<NotificationTemplateView>();

    // query
    const {
        data: notificationTemplates,
        isFetching: isLoading,
        refetch,
    } = useGetNotificationTemplates({
        authorUids: authorFilter,
        severity: severityFilter,
        priority: priorityFilter,
        professionalField: professionalFieldFilter,
        isShared: isSharedFilter,
        search: searchFilter,
        page: currentPage,
        pageSize: parseInt(pageSize.value),
    });

    // mutation
    const deleteNotificationTemplate = useDeleteNotificationTemplate();

    const tableColumns = useMemo(() => {
        const columns: TableColumn<NotificationTemplateView>[] = [
            {
                Header: "NAZIV",
                disableSortBy: true,
                width: 8,
                Cell: ({ row }: { row: Row<NotificationTemplateView> }) => (
                    <span>{row.original.name}</span>
                ),
            },
            {
                Header: "AUTOR",
                disableSortBy: true,
                width: 5,
                Cell: ({ row }: { row: Row<NotificationTemplateView> }) => (
                    <span>{row.original.authorName}</span>
                ),
            },
            {
                Header: "STRUČNO PODRUČJE",
                disableSortBy: true,
                width: 3,
                Cell: ({ row }: { row: Row<NotificationTemplateView> }) => (
                    <span>{row.original.professionalField}</span>
                ),
            },
            {
                Header: "VRSTA PORUKE",
                disableSortBy: true,
                width: 2,
                Cell: ({ row }: { row: Row<NotificationTemplateView> }) => (
                    <span>
                        {
                            NotificationSeverityTranslation[
                                row.original.severity as NotificationSeverity
                            ]
                        }
                    </span>
                ),
            },
            {
                Header: "PRIORITET",
                disableSortBy: true,
                width: 2,
                Cell: ({ row }: { row: Row<NotificationTemplateView> }) => (
                    <span>
                        {
                            NotificationPriorityTranslation[
                                row.original.priority as NotificationPriority
                            ]
                        }
                    </span>
                ),
            },
            {
                Header: "JAVNO",
                disableSortBy: true,
                width: 1,
                className: "actions-cell",
                Cell: ({ row }: { row: Row<NotificationTemplateView> }) => (
                    <span>{row.original.isShared ? "Da" : "Ne"}</span>
                ),
            },
            {
                Header: "UČITAJ",
                disableSortBy: true,
                width: 1,
                className: "actions-cell",
                Cell: ({ row }: { row: Row<NotificationTemplateView> }) => (
                    <Button
                        isIcon
                        size="small"
                        variant="primary-ghost"
                        onClick={() =>
                            history.push(`${AppRoutes.Communication}?templateId=${row.original.id}`)
                        }
                    >
                        <Load />
                    </Button>
                ),
            },
            {
                Header: "UREDI",
                disableSortBy: true,
                width: 1,
                className: "actions-cell",
                Cell: ({ row }: { row: Row<NotificationTemplateView> }) =>
                    profile?.userId === row.original.authorUid ? (
                        <Button
                            isIcon
                            size="small"
                            variant="primary-ghost"
                            onClick={() => {
                                const { original } = row;
                                setSelectedNotificationTemplate(original);
                                setIsTemplateModalOpen(true);
                            }}
                        >
                            <Edit />
                        </Button>
                    ) : null,
            },
            {
                Header: "OBRIŠI",
                disableSortBy: true,
                width: 1,
                className: "actions-cell",
                Cell: ({ row }: { row: Row<NotificationTemplateView> }) =>
                    profile?.userId === row.original.authorUid ? (
                        <Button
                            isIcon
                            size="small"
                            variant="warning-ghost"
                            onClick={() => {
                                setSelectedTemplateId(row.original.id);
                                setIsConfirmationModalOpen(true);
                            }}
                        >
                            <Delete />
                        </Button>
                    ) : null,
            },
        ];

        return columns;
    }, []);

    useEffect(() => {
        if (!notificationTemplates?.notificationTemplatesCount) return;

        const lastPage = Math.ceil(
            notificationTemplates?.notificationTemplatesCount / parseInt(pageSize.value)
        );
        if (lastPage < currentPage) setCurrentPage(lastPage);
    }, [notificationTemplates?.notificationTemplatesCount]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchFilter]);

    return (
        <>
            <div className="f-c g-24 f-1">
                <div className="f-r g-12">
                    <NotificationTemplatesFilters
                        authorFilter={authorFilter}
                        setAuthorFilter={setAuthorFilter}
                        priorityFilter={priorityFilter}
                        setPriorityFilter={setPriorityFilter}
                        severityFilter={severityFilter}
                        setSeverityFilter={setSeverityFilter}
                        professionalFieldFilter={professionalFieldFilter}
                        setProfessionalFieldFilter={setProfessionalFieldFilter}
                        isSharedFilter={isSharedFilter}
                        setIsSharedFilter={setIsSharedFilter}
                        setCurrentPage={setCurrentPage}
                        searchFilter={searchFilter}
                        setSearchFilter={setSearchFilter}
                    />
                    <Button
                        onClick={() => setIsTemplateModalOpen(true)}
                        variant="primary"
                        size="small"
                    >
                        NOVI PREDLOŽAK
                    </Button>
                </div>

                <CustomTable
                    tableType={TableType.NotificationTemplatesTable}
                    columns={tableColumns}
                    flex
                    loading={isLoading}
                    data={notificationTemplates?.notificationTemplatesList ?? []}
                />

                {!isLoading &&
                    Boolean(notificationTemplates) &&
                    notificationTemplates?.notificationTemplatesCount !== 0 && (
                        <Pagination
                            currentPage={currentPage}
                            numberOfPages={Math.ceil(
                                (notificationTemplates?.notificationTemplatesCount ?? 0) /
                                    parseInt(pageSize.value)
                            )}
                            pageNeighbours={1}
                            pageSize={pageSize}
                            onPageSizeChange={v =>
                                handlePageSizeChange(v, setPageSize, setCurrentPage)
                            }
                            handlePageClick={e => handlePageClick(e, setCurrentPage)}
                            handlePageSelect={e => setCurrentPage(e)}
                            next={() => setCurrentPage(p => p + 1)}
                            prev={() => setCurrentPage(p => p - 1)}
                            first={() => setCurrentPage(1)}
                            last={() =>
                                setCurrentPage(
                                    Math.ceil(
                                        notificationTemplates?.notificationTemplatesCount ??
                                            0 / parseInt(pageSize.value)
                                    )
                                )
                            }
                        />
                    )}
            </div>

            <CreateUpdateNotificationTemplateModal
                isActive={isTemplateModalOpen}
                toggleModal={() => {
                    setSelectedNotificationTemplate(undefined);
                    setIsTemplateModalOpen(false);
                }}
                selectedNotificationTemplate={selectedNotificationTemplate}
            />

            <ConfirmationModal
                content="Jeste li sigurni da želite obrisati odabrani predložak?"
                isActive={isConfirmationModalOpen}
                onConfirm={() => {
                    deleteNotificationTemplate.mutateAsync(selectedTemplateId!).then(_ => {
                        if (notificationTemplates?.notificationTemplatesList.length === 1) {
                            setCurrentPage(p => (p !== 0 ? p - 1 : 0));
                        } else {
                            refetch();
                        }
                    });

                    setSelectedTemplateId(null);
                    setIsConfirmationModalOpen(false);
                }}
                onCancel={() => {
                    setSelectedTemplateId(null);
                    setIsConfirmationModalOpen(false);
                }}
            />
        </>
    );
};
